import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import { 
  Calendar as CalendarIcon, 
  ChevronLeft, 
  ChevronRight,
  Briefcase,
  Truck,
  ClipboardCheck,
  Flag,
  X,
  ExternalLink,
  AlertTriangle,
  MapPin,
  Navigation,
  Clock
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Event colors from user spec
const EVENT_COLORS = {
  TRADE: '#3b82f6',        // Blue
  DELIVERY: '#f97316',     // Orange
  INSPECTION: '#8b5cf6',   // Purple
  MILESTONE: '#22c55e',    // Green
  BUFFER: '#94a3b8',       // Gray
  COLLECTION: '#3b82f6',   // Blue (same as trade)
};

// Status colors override
const STATUS_COLORS = {
  blocked: '#ef4444',      // Red
  at_risk: '#f97316',      // Orange
  completed: '#10b981',    // Emerald
  delayed: '#ef4444',      // Red
  cancelled: '#6b7280',    // Gray
};

// Get event icon
const getEventIcon = (type) => {
  switch (type) {
    case 'TRADE': return Briefcase;
    case 'DELIVERY': return Truck;
    case 'COLLECTION': return Truck;
    case 'INSPECTION': return ClipboardCheck;
    case 'MILESTONE': return Flag;
    default: return CalendarIcon;
  }
};

// Get event color based on type and status
const getEventColor = (event) => {
  if (event.status === 'blocked' || event.is_blocked) return STATUS_COLORS.blocked;
  if (event.status === 'at_risk' || event.is_at_risk) return STATUS_COLORS.at_risk;
  if (event.status === 'completed') return STATUS_COLORS.completed;
  if (event.status === 'delayed') return STATUS_COLORS.delayed;
  if (event.status === 'cancelled') return STATUS_COLORS.cancelled;
  return EVENT_COLORS[event.type] || EVENT_COLORS.TRADE;
};

// Event detail modal
function EventModal({ event, onClose, onNavigateToJob }) {
  if (!event) return null;

  const Icon = getEventIcon(event.type);
  const color = getEventColor(event);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="glass-panel p-6 max-w-md w-full rounded-xl" 
        onClick={e => e.stopPropagation()}
        data-testid="calendar-event-modal"
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${color}20`, color }}
            >
              <Icon size={20} />
            </div>
            <div>
              <span 
                className="text-xs font-medium px-2 py-0.5 rounded-full"
                style={{ backgroundColor: `${color}20`, color }}
              >
                {event.type}
              </span>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
            data-testid="close-event-modal-btn"
          >
            <X size={20} />
          </button>
        </div>

        <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">{event.title}</h3>
        
        {event.description && (
          <p className="text-sm text-[var(--text-secondary)] mb-4">{event.description}</p>
        )}

        <div className="space-y-3 mb-4">
          {/* Job Reference */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-[var(--text-secondary)]">Job</span>
            <span className="text-[var(--text-primary)] font-medium">
              {event.job_name}
              {event.job_reference && ` (${event.job_reference})`}
            </span>
          </div>

          {/* Client */}
          {event.client_name && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-[var(--text-secondary)]">Client</span>
              <span className="text-[var(--text-primary)]">{event.client_name}</span>
            </div>
          )}

          {/* Date Range */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-[var(--text-secondary)]">Date</span>
            <span className="text-[var(--text-primary)]">
              {event.start_date === event.end_date 
                ? new Date(event.start_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                : `${new Date(event.start_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - ${new Date(event.end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`
              }
            </span>
          </div>

          {/* Duration */}
          {event.duration_days > 1 && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-[var(--text-secondary)]">Duration</span>
              <span className="text-[var(--text-primary)]">{event.duration_days} days</span>
            </div>
          )}

          {/* Assigned Trade */}
          {event.assigned_trade && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-[var(--text-secondary)]">Assigned To</span>
              <span className="text-[var(--text-primary)]">{event.assigned_trade}</span>
            </div>
          )}

          {/* PO Info */}
          {event.po_number && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-[var(--text-secondary)]">PO Number</span>
              <span className="text-[var(--text-primary)]">{event.po_number}</span>
            </div>
          )}

          {event.supplier_name && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-[var(--text-secondary)]">Supplier</span>
              <span className="text-[var(--text-primary)]">{event.supplier_name}</span>
            </div>
          )}

          {/* Status */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-[var(--text-secondary)]">Status</span>
            <span 
              className="px-2 py-0.5 rounded-full text-xs font-medium capitalize"
              style={{ backgroundColor: `${color}20`, color }}
            >
              {event.status?.replace('_', ' ')}
            </span>
          </div>

          {/* Job Location */}
          {event.job_address && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-[var(--text-secondary)]">Location</span>
              <span className="text-[var(--text-primary)] text-right max-w-[200px] truncate">
                {event.job_address}
              </span>
            </div>
          )}

          {/* Distance from Base */}
          {event.distance_miles && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-[var(--text-secondary)] flex items-center gap-1">
                <Navigation size={12} />
                Distance
              </span>
              <span className="text-[var(--text-primary)]">
                {event.distance_miles} mi (~{event.travel_minutes || '--'}min)
              </span>
            </div>
          )}
        </div>

        {/* Warning for blocked/at-risk */}
        {(event.is_blocked || event.is_at_risk) && event.blocking_reason && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-[var(--status-danger)]/10 text-[var(--status-danger)] mb-4">
            <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
            <span className="text-sm">{event.blocking_reason}</span>
          </div>
        )}

        {/* Get Directions Button */}
        {event.job_address && (
          <a
            href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(event.job_address)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-[var(--bg-secondary)] text-[var(--text-primary)] font-medium hover:bg-[var(--bg-tertiary)] transition-colors mb-2"
            data-testid="get-directions-btn"
          >
            <MapPin size={16} />
            Get Directions
            <ExternalLink size={14} />
          </a>
        )}

        {/* Action Button */}
        <button
          onClick={() => onNavigateToJob(event.job_id)}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[var(--accent-primary)] text-white font-medium hover:bg-[var(--accent-primary-hover)] transition-colors"
          data-testid="view-job-btn"
        >
          <ExternalLink size={16} />
          View Job Details
        </button>
      </div>
    </div>
  );
}

// Legend component
function CalendarLegend() {
  const items = [
    { type: 'TRADE', label: 'Trade Work', color: EVENT_COLORS.TRADE },
    { type: 'DELIVERY', label: 'Delivery', color: EVENT_COLORS.DELIVERY },
    { type: 'INSPECTION', label: 'Inspection', color: EVENT_COLORS.INSPECTION },
    { type: 'MILESTONE', label: 'Milestone', color: EVENT_COLORS.MILESTONE },
  ];

  return (
    <div className="flex flex-wrap gap-4" data-testid="calendar-legend">
      {items.map(item => {
        const Icon = getEventIcon(item.type);
        return (
          <div key={item.type} className="flex items-center gap-2">
            <div 
              className="w-6 h-6 rounded flex items-center justify-center"
              style={{ backgroundColor: `${item.color}20`, color: item.color }}
            >
              <Icon size={14} />
            </div>
            <span className="text-sm text-[var(--text-secondary)]">{item.label}</span>
          </div>
        );
      })}
    </div>
  );
}

// Stats summary
function CalendarStats({ data }) {
  if (!data) return null;

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="calendar-stats">
      <div className="glass-panel p-4 rounded-xl">
        <div className="text-2xl font-bold text-[var(--text-primary)]">{data.total_trade_events || 0}</div>
        <div className="text-sm text-[var(--text-secondary)]">Trade Tasks</div>
      </div>
      <div className="glass-panel p-4 rounded-xl">
        <div className="text-2xl font-bold text-[var(--text-primary)]">{data.total_delivery_events || 0}</div>
        <div className="text-sm text-[var(--text-secondary)]">Deliveries</div>
      </div>
      <div className="glass-panel p-4 rounded-xl">
        <div className="text-2xl font-bold text-[var(--text-primary)]">{data.total_inspection_events || 0}</div>
        <div className="text-sm text-[var(--text-secondary)]">Inspections</div>
      </div>
      <div className="glass-panel p-4 rounded-xl">
        <div className="text-2xl font-bold text-[var(--text-primary)]">{data.total_milestone_events || 0}</div>
        <div className="text-sm text-[var(--text-secondary)]">Milestones</div>
      </div>
    </div>
  );
}

export default function Calendar() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [view, setView] = useState('dayGridMonth'); // Default to month view
  const calendarRef = React.useRef(null);

  // Get org_id from localStorage (set by AuthContext)
  const orgId = localStorage.getItem('org_id');

  // Fetch calendar data
  const fetchCalendarData = useCallback(async (date) => {
    if (!orgId) return;
    
    setLoading(true);
    try {
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      
      const response = await axios.get(`${API}/api/calendar/month`, {
        params: { org_id: orgId, year, month },
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      
      setCalendarData(response.data);
    } catch (error) {
      console.error('Failed to fetch calendar data:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have access to the calendar');
      } else {
        toast.error('Failed to load calendar data');
      }
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchCalendarData(currentDate);
  }, [currentDate, fetchCalendarData]);

  // Transform data to FullCalendar events
  const events = useMemo(() => {
    if (!calendarData?.days) return [];
    
    const eventMap = new Map();
    
    // Flatten events from all days and deduplicate
    calendarData.days.forEach(day => {
      day.events.forEach(event => {
        if (!eventMap.has(event.id)) {
          eventMap.set(event.id, {
            id: event.id,
            title: event.title,
            start: event.start_date,
            end: event.end_date,
            backgroundColor: getEventColor(event),
            borderColor: getEventColor(event),
            textColor: '#ffffff',
            extendedProps: event,
            allDay: true,
          });
        }
      });
    });
    
    return Array.from(eventMap.values());
  }, [calendarData]);

  // Handle event click
  const handleEventClick = (info) => {
    setSelectedEvent(info.event.extendedProps);
  };

  // Navigate to job
  const handleNavigateToJob = (jobId) => {
    setSelectedEvent(null);
    navigate(`/jobs/${jobId}`);
  };

  // Calendar navigation
  const goToPrev = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.prev();
      setCurrentDate(calendarApi.getDate());
    }
  };

  const goToNext = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.next();
      setCurrentDate(calendarApi.getDate());
    }
  };

  const goToToday = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.today();
      setCurrentDate(calendarApi.getDate());
    }
  };

  const changeView = (newView) => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.changeView(newView);
      setView(newView);
    }
  };

  // Format current display date
  const displayDate = useMemo(() => {
    const options = { month: 'long', year: 'numeric' };
    return currentDate.toLocaleDateString('en-GB', options);
  }, [currentDate]);

  return (
    <div className="space-y-6" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[var(--text-primary)]">Company Calendar</h1>
          <p className="text-[var(--text-secondary)] mt-1">View all scheduled jobs, deliveries, and milestones</p>
        </div>
        
        {/* View Controls */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => changeView('dayGridMonth')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              view === 'dayGridMonth' 
                ? 'bg-[var(--accent-primary)] text-white' 
                : 'bg-[var(--bg-secondary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
            }`}
            data-testid="month-view-btn"
          >
            Month
          </button>
          <button
            onClick={() => changeView('timeGridWeek')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              view === 'timeGridWeek' 
                ? 'bg-[var(--accent-primary)] text-white' 
                : 'bg-[var(--bg-secondary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
            }`}
            data-testid="week-view-btn"
          >
            Week
          </button>
          <button
            onClick={() => changeView('timeGridDay')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              view === 'timeGridDay' 
                ? 'bg-[var(--accent-primary)] text-white' 
                : 'bg-[var(--bg-secondary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
            }`}
            data-testid="day-view-btn"
          >
            Day
          </button>
        </div>
      </div>

      {/* Stats Summary */}
      <CalendarStats data={calendarData} />

      {/* Calendar Controls & Legend */}
      <div className="glass-panel p-4 rounded-xl">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
          {/* Navigation */}
          <div className="flex items-center gap-2">
            <button 
              onClick={goToPrev}
              className="p-2 rounded-lg bg-[var(--bg-secondary)] hover:bg-[var(--bg-tertiary)] transition-colors"
              data-testid="calendar-prev-btn"
            >
              <ChevronLeft size={20} className="text-[var(--text-secondary)]" />
            </button>
            <button 
              onClick={goToToday}
              className="px-3 py-1.5 rounded-lg text-sm font-medium bg-[var(--bg-secondary)] hover:bg-[var(--bg-tertiary)] text-[var(--text-primary)] transition-colors"
              data-testid="calendar-today-btn"
            >
              Today
            </button>
            <button 
              onClick={goToNext}
              className="p-2 rounded-lg bg-[var(--bg-secondary)] hover:bg-[var(--bg-tertiary)] transition-colors"
              data-testid="calendar-next-btn"
            >
              <ChevronRight size={20} className="text-[var(--text-secondary)]" />
            </button>
            <h2 className="ml-4 text-lg font-semibold text-[var(--text-primary)]">{displayDate}</h2>
          </div>
          
          {/* Legend */}
          <CalendarLegend />
        </div>

        {/* Calendar */}
        <div className="calendar-container" data-testid="fullcalendar-container">
          {loading ? (
            <div className="flex items-center justify-center h-96">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--accent-primary)]"></div>
            </div>
          ) : (
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin]}
              initialView="dayGridMonth"
              events={events}
              eventClick={handleEventClick}
              headerToolbar={false}
              height="auto"
              dayMaxEvents={3}
              moreLinkClick="popover"
              firstDay={1}
              eventDisplay="block"
              eventTimeFormat={{
                hour: '2-digit',
                minute: '2-digit',
                meridiem: false
              }}
              eventContent={(eventInfo) => {
                const event = eventInfo.event.extendedProps;
                const Icon = getEventIcon(event.type);
                return (
                  <div className="flex items-center gap-1 px-1 py-0.5 overflow-hidden cursor-pointer">
                    <Icon size={12} className="flex-shrink-0" />
                    <span className="truncate text-xs">{eventInfo.event.title}</span>
                  </div>
                );
              }}
            />
          )}
        </div>
      </div>

      {/* Event Modal */}
      {selectedEvent && (
        <EventModal 
          event={selectedEvent} 
          onClose={() => setSelectedEvent(null)}
          onNavigateToJob={handleNavigateToJob}
        />
      )}

      {/* Custom styles for FullCalendar */}
      <style>{`
        .fc {
          --fc-border-color: var(--border-color);
          --fc-page-bg-color: transparent;
          --fc-neutral-bg-color: var(--bg-secondary);
          --fc-today-bg-color: rgba(99, 102, 241, 0.1);
        }
        
        .fc .fc-daygrid-day-number,
        .fc .fc-col-header-cell-cushion {
          color: var(--text-primary);
        }
        
        .fc .fc-day-other .fc-daygrid-day-number {
          color: var(--text-tertiary);
        }
        
        .fc-theme-standard td,
        .fc-theme-standard th {
          border-color: var(--border-color);
        }
        
        .fc .fc-daygrid-day-top {
          flex-direction: row;
          padding: 4px 8px;
        }
        
        .fc .fc-daygrid-day-events {
          padding: 0 4px 4px;
        }
        
        .fc-event {
          border-radius: 4px;
          font-size: 12px;
          border: none;
          box-shadow: 0 1px 2px rgba(0,0,0,0.1);
        }
        
        .fc-event:hover {
          filter: brightness(1.1);
        }
        
        .fc .fc-more-link {
          color: var(--accent-primary);
          font-weight: 500;
        }
        
        .fc .fc-popover {
          background: var(--bg-primary);
          border: 1px solid var(--border-color);
          border-radius: 8px;
          box-shadow: 0 10px 40px rgba(0,0,0,0.2);
        }
        
        .fc .fc-popover-header {
          background: var(--bg-secondary);
          color: var(--text-primary);
          padding: 8px 12px;
          border-radius: 8px 8px 0 0;
        }
        
        .fc .fc-popover-body {
          padding: 8px;
        }
        
        .fc-timegrid-slot-label {
          color: var(--text-secondary);
        }
        
        .fc-timegrid-axis {
          color: var(--text-secondary);
        }
        
        .fc .fc-timegrid-col.fc-day-today {
          background: rgba(99, 102, 241, 0.05);
        }
      `}</style>
    </div>
  );
}
